/**
 * PreviewSidePanel — pinned live preview column for Exclusions / Formula tabs.
 */

import { FC, useMemo } from 'react';
import { AlertCircle, ChevronLeft, ExternalLink, HelpCircle } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';
import { Tooltip } from '@/components/ui/Tooltip';
import { getApiErrorMessage } from '@/lib/api-error';

const APPLY_HELP =
  'Computes optimal weights from your SAVED config and pushes them to the ' +
  'tracker (Binom/Voluum) right now. It uses the saved config, not your live ' +
  'edits — so Save Config first if you just changed the formula or exclusions. ' +
  'Apply does not save anything itself, and it is blocked while pushes are paused.';

import { useFlowOptimizerLivePreview } from '../../../context/FlowOptimizerLivePreviewContext';
import PreviewLiveBadge from '../PreviewLiveBadge';
import PreviewSummary from '../PreviewSummary';
import { FlowTagsProvider } from '../flow-tags-context';
import PreviewCompactLayersPanel from './PreviewCompactLayersPanel';
import {
  previewChipLabel,
  summarizePreviewItems,
} from '../../../utils/preview-summary.util';

export interface PreviewSidePanelProps {
  onOpenPreviewTab: () => void;
  onCollapse: () => void;
}

const PreviewSidePanel: FC<PreviewSidePanelProps> = ({
  onOpenPreviewTab,
  onCollapse,
}) => {
  const { live, result, applied, applyPending, onApply } =
    useFlowOptimizerLivePreview();
  const chip = useMemo(
    () => (result ? previewChipLabel(summarizePreviewItems(result.items)) : null),
    [result],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <header className="shrink-0 space-y-1.5 border-b border-border/60 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <PreviewLiveBadge
            isFetching={live.isFetching}
            isStale={live.isStale}
            compact
          />
          {chip ? (
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {chip}
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[10px]"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onOpenPreviewTab}
              aria-label="Open full preview tab"
            >
              <ExternalLink className="mr-1 h-3 w-3" aria-hidden />
              Full preview
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={onCollapse}
              aria-label="Hide live preview panel"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            className="h-7 text-xs"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onApply}
            disabled={live.isFetching || applyPending}
            loading={applyPending}
          >
            Apply
          </Button>
          <Tooltip content={APPLY_HELP} side="bottom" interactive contentClassName="max-w-sm">
            <HelpCircle
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-label="What Apply does"
            />
          </Tooltip>
        </div>
        {live.error ? (
          // The server names the exact reason (e.g. which static pins collide
          // and where) — show ITS message, not axios' status line.
          <p className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-[11.5px] leading-snug text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              <span className="font-semibold">Preview blocked: </span>
              {getApiErrorMessage(live.error)}
            </span>
          </p>
        ) : null}
      </header>
      {live.isFetching && !result ? (
        <div className="flex flex-1 justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : result ? (
        <>
          <PreviewSummary result={result} applied={applied} />
          <FlowTagsProvider
            items={result.items}
            countryKey={result.countryKey}
            campaignId={result.campaignId}
            provider={result.provider}
          >
            <PreviewCompactLayersPanel result={result} />
          </FlowTagsProvider>
        </>
      ) : (
        <p className="text-[11px] italic text-muted-foreground">
          Edit exclusions or formulas to refresh the preview.
        </p>
      )}
    </div>
  );
};

export default PreviewSidePanel;
